import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { Button } from 'primereact/button';
import { ChecksSideBarFilterType } from './checks-side-bar-filter.type';
import {
  CheckStatusType,
  getCheckStatusDisplayText,
} from '../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

interface AssignmentsSideBarFilterProps {
  onSearch: (filters: ChecksSideBarFilterType) => void;
}

export const ChecksSideBarFilter = observer(
  ({ onSearch }: AssignmentsSideBarFilterProps) => {
    const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
    const [paymentReceiptDateStart, setPaymentReceiptDateStart] =
      useState<Date | null>(null);
    const [paymentReceiptDateEnd, setPaymentReceiptDateEnd] =
      useState<Date | null>(null);
    const [dueDateStart, setDueDateStart] = useState<Date | null>(null);
    const [dueDateEnd, setDueDateEnd] = useState<Date | null>(null);
    const [selectedCheckStatuses, setSelectedCheckStatuses] = useState<
      CheckStatusType[]
    >([]);
    const [docNumber, setDocNumber] = useState<number | null>(null);
    const [checkReferenceId, setCheckReferenceId] = useState<string | null>(
      null,
    );

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranchIds((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#333',
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      dateContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      checkboxContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
      checkboxItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
      },
    };

    const handleSearch = () => {
      const filters: ChecksSideBarFilterType = {
        branchIds: selectedBranchIds,
        paymentCollectionStartDate: paymentReceiptDateStart,
        paymentCollectionEndDate: paymentReceiptDateEnd,
        checkRepaymentStartDate: dueDateStart,
        checkRepaymentEndDate: dueDateEnd,
        checkStatuses: selectedCheckStatuses,
        docNumber,
        checkReferenceId,
      };
      onSearch(filters);
    };

    const handleClearAllFilters = () => {
      setSelectedBranchIds([]);
      setPaymentReceiptDateStart(null);
      setPaymentReceiptDateEnd(null);
      setDueDateStart(null);
      setDueDateEnd(null);
      setSelectedCheckStatuses([]);
      setDocNumber(null);
      setCheckReferenceId(null);
    };

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.header}>סינונים</div>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAllFilters}
          />
        </div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranchIds}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס המחאה"
            items={
              Object.values(CheckStatusType).filter(
                (value) => typeof value === 'string',
              ) as CheckStatusType[]
            }
            selectedItems={selectedCheckStatuses}
            onItemToggle={(status: CheckStatusType) => {
              setSelectedCheckStatuses((prev) =>
                prev.includes(status)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status],
              );
            }}
            getItemLabel={(status) => getCheckStatusDisplayText(status)}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />

          <CustomExpander>
            <div>טווח תאריכי קליטת תשלום</div>
            <div style={styles.dateContainer}>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>מתאריך</label>
                <Calendar
                  value={paymentReceiptDateStart}
                  onChange={(e) => setPaymentReceiptDateStart(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>עד תאריך</label>
                <Calendar
                  value={paymentReceiptDateEnd}
                  onChange={(e) => setPaymentReceiptDateEnd(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>טווח תאריכי פרעון של המחאות</div>
            <div style={styles.dateContainer}>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>מתאריך</label>
                <Calendar
                  value={dueDateStart}
                  onChange={(e) => setDueDateStart(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>עד תאריך</label>
                <Calendar
                  value={dueDateEnd}
                  onChange={(e) => setDueDateEnd(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>סינון לפי מספר</div>
            <div style={styles.dateContainer}>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>מספר מסמך</label>
                <InputNumber
                  value={docNumber}
                  onValueChange={(e) => setDocNumber(e.value ?? null)}
                  useGrouping={false}
                  min={1}
                  style={{ width: '100%', direction: 'ltr' }}
                  inputStyle={{
                    width: '100%',
                    direction: 'rtl',
                    textAlign: 'right',
                  }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>אסמכתא המחאה</label>
                <InputText
                  value={checkReferenceId ?? ''}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, '');
                    setCheckReferenceId(digitsOnly ? digitsOnly : null);
                  }}
                  keyfilter="int"
                  style={{
                    width: '100%',
                    direction: 'rtl',
                    textAlign: 'right',
                  }}
                />
              </div>
            </div>
          </CustomExpander>
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  },
);
